import { useEffect, useState, useCallback, useRef } from 'react';
import { storeService } from '../services/storeService';
import { StoreCatalogItem, StoreCatalogFilter, StoreCategory, StoreColor } from '../models';
import { ProductCard } from '../components/ui/ProductCard';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { Pagination } from '../components/ui/Pagination';
import { ColorPickerFilter } from '../components/ui/ColorPickerFilter';
import { VariationFilter } from '../components/ui/VariationFilter';
import { PriceRangeSlider } from '../components/ui/PriceRangeSlider';
import { SearchableSelect, SelectOption } from '../components/ui/SearchableSelect';
import { useInitEffect } from '../hooks/useInitEffect';
import { useCatalogFilters, CatalogFilters } from '../hooks/useCatalogFilters';
import { useScrollToTopOnChange } from '../hooks/useScrollToTopOnChange';
import { CHIP_BASE_CLASS, CHIP_ACTIVE_CLASS, CHIP_INACTIVE_CLASS } from '../constants/chipStyles';
import { GenderAccent } from '../components/catalog/GenderAccent';

const PAGE_SIZE = 24;

// Não há endpoint de preço máximo do catálogo: usamos um teto fixo alto o suficiente para cobrir os produtos.
const PRICE_MIN = 0;
const PRICE_MAX = 500;

interface GenderOption {
  label: string;
  value: number | undefined;
}

const GENDER_OPTIONS: GenderOption[] = [
  { label: 'Todos', value: undefined },
  { label: 'Meninos', value: 1 },
  { label: 'Meninas', value: 2 },
  { label: 'Unissex', value: 4 },
];

const GENDER_ACTIVE_CLASS: Record<number, string> = {
  1: 'bg-olivine/15 border-olivine text-olivine-dark font-medium',
  2: 'bg-champagne border-chocolate text-chocolate font-medium',
  4: 'bg-damasco/20 border-damasco text-chocolate-dark font-medium',
};

interface SortOption {
  label: string;
  sortBy?: string;
  sortDirection?: string;
}

const SORT_OPTIONS: SortOption[] = [
  { label: 'Mais recentes', sortBy: 'createdAt', sortDirection: 'desc' },
  { label: 'Relevância' },
  { label: 'Nome A-Z', sortBy: 'name', sortDirection: 'asc' },
  { label: 'Nome Z-A', sortBy: 'name', sortDirection: 'desc' },
  { label: 'Menor preço', sortBy: 'price', sortDirection: 'asc' },
  { label: 'Maior preço', sortBy: 'price', sortDirection: 'desc' },
];

const sortSelectOptions: SelectOption[] = SORT_OPTIONS.map((opt, idx) => ({ id: idx, label: opt.label }));

interface GenderChipsProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

function GenderChips({ value, onChange }: GenderChipsProps) {
  return (
    <>
      {GENDER_OPTIONS.map((opt) => {
        const isActive = value === opt.value;
        const activeClass = (opt.value !== undefined && GENDER_ACTIVE_CLASS[opt.value]) || CHIP_ACTIVE_CLASS;
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(opt.value)}
            className={[CHIP_BASE_CLASS, isActive ? activeClass : CHIP_INACTIVE_CLASS].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </>
  );
}

interface PromotionChipProps {
  value: boolean;
  onChange: (value: boolean) => void;
  locked?: boolean;
}

function PromotionChip({ value, onChange, locked }: PromotionChipProps) {
  return (
    <button
      type="button"
      disabled={locked}
      onClick={() => onChange(!value)}
      title={locked ? 'Filtro fixo durante a campanha promocional' : undefined}
      className={[
        CHIP_BASE_CLASS,
        value ? CHIP_ACTIVE_CLASS : CHIP_INACTIVE_CLASS,
        locked ? 'cursor-default' : '',
      ].join(' ')}
    >
      Promoção
    </button>
  );
}

interface CategoryChipsProps {
  categories: StoreCategory[];
  selectedCategoryIds: number[];
  onCategoryChange: (ids: number[]) => void;
}

function CategoryChips({ categories, selectedCategoryIds, onCategoryChange }: CategoryChipsProps) {
  const handleToggle = (categoryId: number) => {
    if (selectedCategoryIds.includes(categoryId)) {
      onCategoryChange(selectedCategoryIds.filter((id) => id !== categoryId));
    } else {
      onCategoryChange([...selectedCategoryIds, categoryId]);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-text-light font-medium mr-1">Categoria:</span>
      {categories.map((category) => {
        const isSelected = selectedCategoryIds.includes(category.id);
        return (
          <button
            key={category.id}
            type="button"
            onClick={() => handleToggle(category.id)}
            className={[CHIP_BASE_CLASS, isSelected ? CHIP_ACTIVE_CLASS : CHIP_INACTIVE_CLASS].join(' ')}
          >
            {category.name}
          </button>
        );
      })}
    </div>
  );
}

interface ColorAndPriceFiltersProps {
  colors: StoreColor[];
  filters: CatalogFilters;
  className?: string;
}

function ColorAndPriceFilters({ colors, filters, className }: ColorAndPriceFiltersProps) {
  const minValue = filters.minPriceInput !== '' ? Number(filters.minPriceInput) : PRICE_MIN;
  const maxValue = filters.maxPriceInput !== '' ? Number(filters.maxPriceInput) : PRICE_MAX;

  return (
    <div className={className}>
      {colors.length > 0 && (
        <ColorPickerFilter colors={colors} value={filters.colorId} onChange={filters.handleColorChange} />
      )}

      <div className="w-full max-w-xs sm:w-56">
        <span className="block text-xs text-text-medium font-medium mb-2">
          R$ {minValue} a R$ {maxValue}
        </span>
        <PriceRangeSlider
          min={PRICE_MIN}
          max={PRICE_MAX}
          minValue={minValue}
          maxValue={maxValue}
          onMinChange={(value) => filters.handleMinPriceChange(String(value))}
          onMaxChange={(value) => filters.handleMaxPriceChange(String(value))}
        />
      </div>
    </div>
  );
}

interface SecondaryCatalogFiltersProps {
  categories: StoreCategory[];
  filters: CatalogFilters;
  className?: string;
}

function SecondaryCatalogFilters({ categories, filters, className }: SecondaryCatalogFiltersProps) {
  return (
    <div className={className}>
      {categories.length > 0 && (
        <CategoryChips
          categories={categories}
          selectedCategoryIds={filters.categoryIds}
          onCategoryChange={filters.handleCategoryChange}
        />
      )}
      <VariationFilter
        selectedTypeId={filters.variationTypeId}
        onTypeChange={filters.handleVariationTypeChange}
        values={filters.variationValues}
        onChange={filters.handleVariationChange}
      />
    </div>
  );
}

interface FilterPanelContentProps {
  categories: StoreCategory[];
  colors: StoreColor[];
  filters: CatalogFilters;
  className?: string;
}

function FilterPanelContent({ categories, colors, filters, className }: FilterPanelContentProps) {
  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-text-light font-medium mr-1">Gênero:</span>
        <GenderChips value={filters.gender} onChange={filters.handleGenderChange} />
        <PromotionChip value={filters.hasPromotion} onChange={filters.handlePromotionChange} locked={filters.promotionLocked} />
      </div>

      <ColorAndPriceFilters colors={colors} filters={filters} className="flex flex-wrap items-center gap-3" />

      <SecondaryCatalogFilters
        categories={categories}
        filters={filters}
        className="flex flex-col gap-4"
      />
    </div>
  );
}

const PAGE_STEP = 10;

function SidebarFilterSearch({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative mb-2">
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-champagne-dark focus:outline-none focus:border-chocolate bg-champagne-light"
      />
    </div>
  );
}

interface ExpandableCategoryChipsProps {
  categories: StoreCategory[];
  selectedCategoryIds: number[];
  onCategoryChange: (ids: number[]) => void;
}

function ExpandableCategoryChips({ categories, selectedCategoryIds, onCategoryChange }: ExpandableCategoryChipsProps) {
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);

  const filtered = query.trim()
    ? categories.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
    : categories;
  const visible = filtered.slice(0, visibleCount);
  const hiddenCount = filtered.length - visible.length;

  const handleSearchChange = (v: string) => {
    setQuery(v);
    setVisibleCount(PAGE_STEP);
  };

  const handleToggle = (categoryId: number) => {
    if (selectedCategoryIds.includes(categoryId)) {
      onCategoryChange(selectedCategoryIds.filter((id) => id !== categoryId));
    } else {
      onCategoryChange([...selectedCategoryIds, categoryId]);
    }
  };

  return (
    <div>
      <span className="text-sm text-text-light font-medium mb-2 block">Categoria</span>
      {categories.length > PAGE_STEP && (
        <SidebarFilterSearch value={query} onChange={handleSearchChange} placeholder="Buscar categoria..." />
      )}
      {filtered.length === 0 ? (
        <p className="text-sm text-text-light py-1">Nenhuma categoria encontrada</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {visible.map((category) => {
            const isSelected = selectedCategoryIds.includes(category.id);
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleToggle(category.id)}
                className={[CHIP_BASE_CLASS, isSelected ? CHIP_ACTIVE_CLASS : CHIP_INACTIVE_CLASS].join(' ')}
              >
                {category.name}
              </button>
            );
          })}
        </div>
      )}
      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setVisibleCount((v) => v + PAGE_STEP)}
          className="mt-2 text-sm text-chocolate font-medium hover:text-chocolate-dark transition-colors"
        >
          Ver mais
        </button>
      ) : (
        visibleCount > PAGE_STEP && filtered.length > PAGE_STEP && (
          <button
            type="button"
            onClick={() => setVisibleCount(PAGE_STEP)}
            className="mt-2 text-sm text-chocolate font-medium hover:text-chocolate-dark transition-colors"
          >
            Ver menos
          </button>
        )
      )}
    </div>
  );
}

interface ExpandableColorSwatchesProps {
  colors: StoreColor[];
  value: number | undefined;
  onChange: (id: number | undefined) => void;
}

function ExpandableColorSwatches({ colors, value, onChange }: ExpandableColorSwatchesProps) {
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);

  const filtered = query.trim()
    ? colors.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
    : colors;
  const visible = filtered.slice(0, visibleCount);
  const hiddenCount = filtered.length - visible.length;

  const handleSearchChange = (v: string) => {
    setQuery(v);
    setVisibleCount(PAGE_STEP);
  };

  return (
    <div>
      <span className="text-sm text-text-light font-medium mb-2 block">Cor</span>
      {colors.length > PAGE_STEP && (
        <SidebarFilterSearch value={query} onChange={handleSearchChange} placeholder="Buscar cor..." />
      )}
      {filtered.length === 0 ? (
        <p className="text-sm text-text-light py-1">Nenhuma cor encontrada</p>
      ) : (
        <div className="grid grid-cols-4 gap-x-2 gap-y-3">
          {visible.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange(value === c.id ? undefined : c.id)}
              title={c.name}
              aria-pressed={value === c.id}
              className="flex flex-col items-center gap-1 group"
            >
              <span
                className={`w-9 h-9 rounded-full border-2 block transition-all ${
                  value === c.id
                    ? 'border-chocolate scale-110 shadow-md'
                    : 'border-champagne-dark group-hover:border-chocolate/60 group-hover:scale-105'
                }`}
                style={{ backgroundColor: c.hexadecimal }}
              />
              <span className={`text-xs leading-tight text-center truncate max-w-full transition-colors ${
                value === c.id ? 'text-chocolate font-medium' : 'text-text-light'
              }`}>
                {c.name}
              </span>
            </button>
          ))}
        </div>
      )}
      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setVisibleCount((v) => v + PAGE_STEP)}
          className="mt-2 text-sm text-chocolate font-medium hover:text-chocolate-dark transition-colors"
        >
          Ver mais
        </button>
      ) : (
        visibleCount > PAGE_STEP && filtered.length > PAGE_STEP && (
          <button
            type="button"
            onClick={() => setVisibleCount(PAGE_STEP)}
            className="mt-2 text-sm text-chocolate font-medium hover:text-chocolate-dark transition-colors"
          >
            Ver menos
          </button>
        )
      )}
    </div>
  );
}

function SidebarFilterContent({ categories, colors, filters }: FilterPanelContentProps) {
  const minValue = filters.minPriceInput !== '' ? Number(filters.minPriceInput) : PRICE_MIN;
  const maxValue = filters.maxPriceInput !== '' ? Number(filters.maxPriceInput) : PRICE_MAX;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <span className="text-sm text-text-light font-medium mb-2 block">Gênero</span>
        <div className="flex flex-wrap gap-2">
          <GenderChips value={filters.gender} onChange={filters.handleGenderChange} />
        </div>
      </div>

      <div>
        <PromotionChip value={filters.hasPromotion} onChange={filters.handlePromotionChange} locked={filters.promotionLocked} />
      </div>

      {categories.length > 0 && (
        <ExpandableCategoryChips
          categories={categories}
          selectedCategoryIds={filters.categoryIds}
          onCategoryChange={filters.handleCategoryChange}
        />
      )}

      {colors.length > 0 && (
        <ExpandableColorSwatches colors={colors} value={filters.colorId} onChange={filters.handleColorChange} />
      )}

      <VariationFilter
        selectedTypeId={filters.variationTypeId}
        onTypeChange={filters.handleVariationTypeChange}
        values={filters.variationValues}
        onChange={filters.handleVariationChange}
      />

      <div>
        <span className="block text-sm text-text-medium font-medium mb-2">
          R$ {minValue} a R$ {maxValue}
        </span>
        <PriceRangeSlider
          min={PRICE_MIN}
          max={PRICE_MAX}
          minValue={minValue}
          maxValue={maxValue}
          onMinChange={(value) => filters.handleMinPriceChange(String(value))}
          onMaxChange={(value) => filters.handleMaxPriceChange(String(value))}
        />
      </div>
    </div>
  );
}

interface MoreFiltersPanelProps {
  categories: StoreCategory[];
  colors: StoreColor[];
  filters: CatalogFilters;
  className?: string;
}

function MoreFiltersPanel({ categories, colors, filters, className }: MoreFiltersPanelProps) {
  return (
    <div className={className}>
      <ColorAndPriceFilters colors={colors} filters={filters} className="flex flex-wrap items-center gap-3" />
      <VariationFilter
        selectedTypeId={filters.variationTypeId}
        onTypeChange={filters.handleVariationTypeChange}
        values={filters.variationValues}
        onChange={filters.handleVariationChange}
      />
      {categories.length > 0 && (
        <CategoryChips
          categories={categories}
          selectedCategoryIds={filters.categoryIds}
          onCategoryChange={filters.handleCategoryChange}
        />
      )}
    </div>
  );
}

export function CatalogPage() {
  const [items, setItems] = useState<StoreCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [colors, setColors] = useState<StoreColor[]>([]);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(true);

  const filters = useCatalogFilters();
  const previousFilterRef = useRef<StoreCatalogFilter | null>(null);

  useInitEffect(() => {
    storeService.getCategories().then(setCategories).catch(() => {});
    storeService.getColors().then(setColors).catch(() => {});
  });

  const fetchCatalog = useCallback(async (filter: StoreCatalogFilter) => {
    setLoading(true);
    try {
      const data = await storeService.getCatalog(filter);
      setItems(data.items);
      setTotal(data.count);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectedSort = SORT_OPTIONS[filters.selectedSortIndex];

  useScrollToTopOnChange(filters.page);

  useEffect(() => {
    const currentFilter: StoreCatalogFilter = {
      pageNumber: filters.page,
      pageSize: PAGE_SIZE,
      search: filters.search || undefined,
      categoryIds: filters.categoryIds.length > 0 ? filters.categoryIds : undefined,
      collectionId: filters.collectionId,
      colorId: filters.colorId,
      variationTypeId: filters.variationTypeId,
      variationValues: filters.variationValues.length > 0 ? filters.variationValues : undefined,
      gender: filters.gender,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      hasPromotion: filters.hasPromotion || undefined,
      sortBy: selectedSort.sortBy,
      sortDirection: selectedSort.sortDirection,
    };

    if (JSON.stringify(currentFilter) !== JSON.stringify(previousFilterRef.current)) {
      previousFilterRef.current = currentFilter;
      fetchCatalog(currentFilter);
    }
  }, [filters.page, filters.search, filters.categoryIds, filters.collectionId, filters.colorId, filters.variationTypeId, filters.variationValues, filters.gender, filters.minPrice, filters.maxPrice, filters.hasPromotion, selectedSort, fetchCatalog]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const activeSecondaryFiltersCount = [
    filters.gender !== undefined,
    filters.hasPromotion,
    filters.colorId !== undefined,
    filters.minPriceInput !== '',
    filters.maxPriceInput !== '',
    filters.categoryIds.length > 0,
    filters.variationValues.length > 0,
  ].filter(Boolean).length;

  const activeMoreFiltersCount = [
    filters.colorId !== undefined,
    filters.minPriceInput !== '',
    filters.maxPriceInput !== '',
    filters.variationValues.length > 0,
    filters.categoryIds.length > 0,
  ].filter(Boolean).length;

  return (
    <div className="max-w-[1600px] mx-auto px-4 py-10">
      <GenderAccent gender={filters.gender} />
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-display font-semibold text-text-dark">Catálogo</h1>
        <p className="text-sm text-text-light mt-1">
          {total} {total === 1 ? 'produto encontrado' : 'produtos encontrados'}
        </p>
      </div>

      <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-6 lg:items-start">
        <aside className="hidden lg:block">
          <div className="bg-white rounded-2xl shadow-sm p-4 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-text-dark text-base">Filtros</h2>
              {filters.hasFilters && (
                <button
                  type="button"
                  onClick={filters.clearFilters}
                  className="text-sm text-chocolate font-medium hover:text-chocolate-dark transition-colors"
                >
                  Limpar
                </button>
              )}
            </div>
            <SidebarFilterContent categories={categories} colors={colors} filters={filters} />
          </div>
        </aside>

        <div>
          <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">

            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <div className="relative flex-1">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar por nome ou descrição..."
                  value={filters.searchInput}
                  onChange={(e) => filters.handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-9 py-2.5 rounded-full border border-champagne-dark text-text-dark placeholder-text-light bg-champagne-light/40 focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate focus:bg-white transition-colors text-sm"
                />
                {filters.searchInput && (
                  <button
                    onClick={() => filters.handleSearchChange('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="self-start sm:self-auto w-full sm:w-48">
                <SearchableSelect
                  options={sortSelectOptions}
                  value={filters.selectedSortIndex}
                  onChange={(idx) => filters.handleSortChange(idx ?? 0)}
                  required
                  className="text-xs"
                />
              </div>

              {filters.hasFilters && (
                <button
                  onClick={filters.clearFilters}
                  className="lg:hidden flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-champagne-dark text-xs text-text-medium hover:text-chocolate hover:border-chocolate transition-colors whitespace-nowrap"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Limpar filtros
                </button>
              )}
            </div>

            <div className="hidden sm:block lg:hidden">
              <div className="flex flex-wrap items-center gap-2">
                <GenderChips value={filters.gender} onChange={filters.handleGenderChange} />

                <PromotionChip value={filters.hasPromotion} onChange={filters.handlePromotionChange} locked={filters.promotionLocked} />

                <span className="w-px h-4 bg-champagne-dark" aria-hidden="true" />

                <button
                  type="button"
                  onClick={() => setShowMoreFilters((v) => !v)}
                  className={[
                    'flex items-center gap-1.5',
                    CHIP_BASE_CLASS,
                    showMoreFilters ? CHIP_ACTIVE_CLASS : CHIP_INACTIVE_CLASS,
                  ].join(' ')}
                >
                  Mais filtros
                  {activeMoreFiltersCount > 0 && (
                    <span className="bg-chocolate text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">
                      {activeMoreFiltersCount}
                    </span>
                  )}
                  <svg
                    className={`w-3 h-3 transition-transform duration-150 ${showMoreFilters ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {showMoreFilters && (
                <MoreFiltersPanel
                  categories={categories}
                  colors={colors}
                  filters={filters}
                  className="flex flex-col gap-4 mt-4 pt-4 border-t border-champagne"
                />
              )}
            </div>
          </div>

          <div>
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="Nenhum produto encontrado"
              description={filters.hasFilters ? 'Tente ajustar os filtros.' : 'Nenhum produto disponível no momento.'}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    colorId={filters.colorId}
                    variationValue={filters.variationValues[0]}
                  />
                ))}
              </div>

              <Pagination page={filters.page} totalPages={totalPages} onChange={filters.setPage} />
            </>
          )}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowFilterSheet(true)}
        className="sm:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-text-dark text-white text-sm font-medium pl-4 pr-4 py-3 rounded-full shadow-lg"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 4h18M6 12h12M10 20h4" />
        </svg>
        Filtrar
        {activeSecondaryFiltersCount > 0 && (
          <span className="bg-white text-text-dark text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
            {activeSecondaryFiltersCount}
          </span>
        )}
      </button>

      <div
        className={`sm:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
          showFilterSheet ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!showFilterSheet}
      >
        <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilterSheet(false)} />

        <div
          className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto transition-transform duration-300 ease-out ${
            showFilterSheet ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="flex justify-center pt-2">
            <span className="w-10 h-1.5 bg-champagne-dark rounded-full" />
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-b border-champagne">
            <h2 className="font-display font-semibold text-text-dark text-base">Filtros</h2>
            <div className="flex items-center gap-4">
              {filters.hasFilters && (
                <button
                  type="button"
                  onClick={filters.clearFilters}
                  className="text-xs text-chocolate font-medium"
                >
                  Limpar
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowFilterSheet(false)}
                aria-label="Fechar filtros"
                className="text-text-light hover:text-text-dark"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <FilterPanelContent
            categories={categories}
            colors={colors}
            filters={filters}
            className="flex flex-col gap-4 px-4 py-4"
          />

          <div className="sticky bottom-0 bg-white border-t border-champagne px-4 py-3">
            <button
              type="button"
              onClick={() => setShowFilterSheet(false)}
              className="w-full bg-chocolate text-white text-sm font-semibold py-3 rounded-lg"
            >
              Ver {total} produtos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
